/**
 * SHELL — pointer drag -> steer. Input is the Shell's responsibility.
 *
 * We capture a single active pointer (mouse OR touch), use pointer capture so the
 * drag keeps tracking outside the element, and prevent scroll/zoom/pull-to-refresh
 * during a run (`touch-action: none` on the surface + preventDefault here).
 *
 * The game is LATERAL-ONLY: steering comes purely from the HORIZONTAL drag delta
 * from the press origin, scaled by a radius and shaped (deadzone + mild expo) into
 * `steerX` in [-1, 1]. Vertical drag is ignored entirely — `steerY` is always 0.
 * Core receives this RAW steer each tick and does the int16 quantization into the
 * replay — we never quantize here.
 *
 * Convention: steerX = left/right (screen X), right positive. steerY is unused.
 */

export interface Steer {
  /** [-1, 1], right positive. */
  readonly steerX: number;
  /** Always 0 — vertical drag does not steer (Core ignores this axis). */
  readonly steerY: number;
}

export const ZERO_STEER: Steer = { steerX: 0, steerY: 0 };

/**
 * Horizontal drag magnitude (as a fraction of the radius) within this much of the
 * origin maps to zero steer, so a resting or slightly-jittery finger doesn't
 * steer. Past the deadzone the response is re-scaled to start from 0 (no jump).
 */
const STEER_DEADZONE = 0.08;

/**
 * Expo / response curve exponent applied to the 1-D deadzoned magnitude. ~1.2 is
 * close to linear for a predictable mid-range; a full drag (magnitude 1) still
 * reaches full deflection since 1^EXPO === 1.
 */
const STEER_EXPO = 1.2;

export interface InputController {
  /** The current steer, sampled by the game loop each tick. */
  readonly steer: Steer;
  /** True while a pointer is actively dragging. */
  readonly active: boolean;
  /** Detach all listeners and release capture. */
  dispose(): void;
}

export interface InputOptions {
  /**
   * Drag distance (CSS px) that maps to full deflection (|steer| = 1).
   * Defaults to a fraction of the smaller viewport edge so it feels right on any
   * screen size.
   */
  readonly radiusPx?: number;
  /** Called on the first interaction (to hide the steer hint, etc.). */
  readonly onFirstInput?: () => void;
  /** Called whenever active state changes (for HUD affordances). */
  readonly onActiveChange?: (active: boolean) => void;
}

/**
 * Shape the raw (radius-scaled) HORIZONTAL drag delta into a 1-D steer so small/
 * jittery drags don't over-steer, while preserving the left/right sign:
 *
 *   1. Clamp magnitude to [0, 1]:  mag = min(|dx|, 1).
 *   2. Deadzone + re-scale:  m' = (mag - DEADZONE) / (1 - DEADZONE)
 *      so just past the deadzone the response starts from 0 (no jump).
 *   3. Expo:  m'' = m'^EXPO  — predictable mid-range, full drag still reaches 1.
 *
 * The sign of `dx` is reapplied to the shaped magnitude. `steerY` is always 0:
 * vertical drag is ignored (Core is lateral-only), so a purely vertical drag
 * yields zero steer.
 */
function shapeSteer(dx: number): Steer {
  const mag = Math.min(Math.abs(dx), 1);
  if (mag <= STEER_DEADZONE) return ZERO_STEER;
  const deadzoned = (mag - STEER_DEADZONE) / (1 - STEER_DEADZONE);
  const shaped = Math.pow(deadzoned, STEER_EXPO);
  return { steerX: Math.sign(dx) * shaped, steerY: 0 };
}

/**
 * Attach drag-to-steer to a surface element (typically the Render canvas).
 * Returns a controller whose `.steer` the game loop reads every tick.
 */
export function createInputController(surface: HTMLElement, opts: InputOptions = {}): InputController {
  let steer: Steer = ZERO_STEER;
  let active = false;
  let pointerId: number | null = null;
  let originX = 0;
  let firstFired = false;

  const radius = (): number => opts.radiusPx ?? Math.max(80, Math.min(window.innerWidth, window.innerHeight) * 0.28);

  const setActive = (next: boolean): void => {
    if (active === next) return;
    active = next;
    opts.onActiveChange?.(next);
  };

  const onDown = (ev: PointerEvent): void => {
    if (pointerId !== null) return; // ignore secondary pointers (no pinch-steer)
    pointerId = ev.pointerId;
    originX = ev.clientX;
    steer = ZERO_STEER;
    setActive(true);
    try {
      surface.setPointerCapture(ev.pointerId);
    } catch {
      /* capture unsupported — listeners on the surface still work */
    }
    if (!firstFired) {
      firstFired = true;
      opts.onFirstInput?.();
    }
    ev.preventDefault();
  };

  const onMove = (ev: PointerEvent): void => {
    if (ev.pointerId !== pointerId) return;
    const r = radius();
    // Lateral-only: only the horizontal delta steers. Vertical drag is ignored.
    const dx = (ev.clientX - originX) / r;
    // Shape (1-D deadzone + expo) before exposing as `.steer`; preserves the
    // left/right sign. Core records what it receives.
    steer = shapeSteer(dx);
    ev.preventDefault();
  };

  const end = (ev: PointerEvent): void => {
    if (ev.pointerId !== pointerId) return;
    pointerId = null;
    steer = ZERO_STEER; // release recenters the stick
    setActive(false);
    try {
      surface.releasePointerCapture(ev.pointerId);
    } catch {
      /* ignore */
    }
    ev.preventDefault();
  };

  // Block native gestures that would otherwise steal the drag.
  const blockGesture = (ev: Event): void => ev.preventDefault();

  surface.addEventListener('pointerdown', onDown, { passive: false });
  surface.addEventListener('pointermove', onMove, { passive: false });
  surface.addEventListener('pointerup', end, { passive: false });
  surface.addEventListener('pointercancel', end, { passive: false });
  surface.addEventListener('touchstart', blockGesture, { passive: false });
  surface.addEventListener('touchmove', blockGesture, { passive: false });
  surface.addEventListener('contextmenu', blockGesture);

  return {
    get steer() {
      return steer;
    },
    get active() {
      return active;
    },
    dispose() {
      surface.removeEventListener('pointerdown', onDown);
      surface.removeEventListener('pointermove', onMove);
      surface.removeEventListener('pointerup', end);
      surface.removeEventListener('pointercancel', end);
      surface.removeEventListener('touchstart', blockGesture);
      surface.removeEventListener('touchmove', blockGesture);
      surface.removeEventListener('contextmenu', blockGesture);
      if (pointerId !== null) {
        try {
          surface.releasePointerCapture(pointerId);
        } catch {
          /* ignore */
        }
      }
    },
  };
}
